import React, { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ColorPalette from '../../colorpalette/ColorPalette';
import Toast from '../support/Toast';
import { DataProviderInstance } from '../../api/DataProvider';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { isOwner } from '../../utils/RoleManager';
import CompanyClosureDialog from './CompanyClosureDialog';
import CompanyClosureStatusCard from './CompanyClosureStatusCard';
import useCompanyClosureRevert from './useCompanyClosureRevert';
import { CLOSURE_GRACE_DAYS, isClosureOpen, scrollToExport } from './companyClosure';

// Botão vermelho contornado, como o DANGER_BUTTON_SX do PatientPrivacyPanel
// (o tema força contained/outlined em azul, então o estilo vai por sx).
const DANGER_BUTTON_SX = {
    color: 'error.main',
    borderColor: 'error.main',
    borderRadius: '8px',
    textTransform: 'none',
    height: '40px',
    fontWeight: 500,
    flexShrink: 0,
    '&:hover': {
        borderColor: 'error.main',
        backgroundColor: ColorPalette.errorLight,
    },
    '@media (max-width: 600px)': { width: '100%' },
};

/**
 * Seção "Encerramento da conta" em Backup e dados (LGPD, checkpoint 4). Só o
 * dono vê. Sem ciclo: explicação curta e "Encerrar conta" (quando o backend
 * libera: closure_can_request). Com ciclo aberto: card de status.
 * `exportHistory` e `onExportCreated` vêm da página (Export.js): o passo
 * Exportar do diálogo inicia a exportação completa, a página recarrega o
 * histórico e baixa o arquivo sozinha ao terminar.
 * Especificação: docs/features/lgpd-eliminacao/README.md (checkpoint 4)
 */
function CompanyClosureSection({ exportHistory, onExportCreated }) {
    // isOwner() lê o DataProvider (hook): só no corpo do componente
    const owner = isOwner();
    const { CompanyConfig } = DataProviderInstance();
    const { closure, closureCanRequest, refreshSubscription } = useSubscription();
    const { reverting, revert } = useCompanyClosureRevert();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [revertError, setRevertError] = useState(null);
    const [toast, setToast] = useState(null);

    const open = isClosureOpen(closure);
    if (!owner || (!open && !closureCanRequest)) {
        return <Toast open={!!toast} message={toast || ''} severity="success" onClose={() => setToast(null)} />;
    }

    const handleRevert = async () => {
        setRevertError(null);
        const result = await revert();
        if (result.success) {
            setToast('Encerramento cancelado. A conta continua ativa.');
        } else {
            setRevertError(result.error);
        }
    };

    const handleRequested = () => {
        refreshSubscription(true);
        setToast('Encerramento solicitado. Enviamos os detalhes por e-mail.');
    };

    return (
        <>
            {open ? (
                <CompanyClosureStatusCard
                    closure={closure}
                    reverting={reverting}
                    revertError={revertError}
                    onExport={scrollToExport}
                    onRevert={handleRevert}
                />
            ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Encerramento da conta
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: '240px' }}>
                            <EventBusyOutlinedIcon fontSize="small" sx={{ mt: '2px', color: ColorPalette.error }} />
                            <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                                {`Encerra a conta e elimina todos os dados depois de uma carência de ${CLOSURE_GRACE_DAYS} dias. `}
                                A assinatura deixa de ser renovada e, até o fim da carência, dá para exportar os dados.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            disableElevation
                            startIcon={<EventBusyOutlinedIcon fontSize="small" />}
                            onClick={() => setDialogOpen(true)}
                            sx={DANGER_BUTTON_SX}
                        >
                            Encerrar conta
                        </Button>
                    </Box>
                </Paper>
            )}

            <CompanyClosureDialog
                open={dialogOpen}
                companyName={CompanyConfig?.Name}
                exportHistory={exportHistory}
                onClose={() => setDialogOpen(false)}
                onDone={handleRequested}
                onExportCreated={onExportCreated}
            />
            <Toast open={!!toast} message={toast || ''} severity="success" onClose={() => setToast(null)} />
        </>
    );
}

export default CompanyClosureSection;
